// The Inbox's data, shared by the top-bar badge and the Inbox page: decisions built from the engine's published actions and
// firm proposal documents (lib/decisions.ts), refreshed on Realtime changes, plus per-viewer snoozes (a convenience kept in
// the browser — snoozing never changes anything in the record).
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import type { DecisionItem } from "../data/types";
import { mayRequest } from "../lib/commands";
import { INBOX_FIRM_KINDS, buildDecisions } from "../lib/decisions";
import { useLoad, usePref, useRealtime, type Loaded } from "../lib/hooks";
import { useApp } from "./context";

interface DecisionsCtx { state: Loaded<DecisionItem[]>; open: DecisionItem[]; snoozed: DecisionItem[]; snooze: (id: string, hours?: number) => void; unsnooze: (id: string) => void }
const Ctx = createContext<DecisionsCtx | null>(null);

export function useDecisions(): DecisionsCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDecisions outside <DecisionsProvider>");
  return c;
}

export function DecisionsProvider({ children }: { children: ReactNode }) {
  const { ds, me, catalogue } = useApp();
  const state = useLoad(async () => {
    if (me.role === "client") return [];
    const [actions, engagements, ...firm] = await Promise.all([ds.myActions(), ds.engagements(), ...INBOX_FIRM_KINDS.map((k) => ds.firmModel(k))]);
    const all = buildDecisions(actions, firm, engagements, catalogue);
    // "Waiting on you" means you may make the decision: firm proposals whose accept command needs another role stay with
    // that role (actions are already addressed to a person by the engine).
    return all.filter((d) => d.source === "action" || mayRequest(catalogue.get(d.decision.accept?.command ?? d.command), me, d.engagementId ? "engagement" : "firm").ok);
  }, [ds, me.userId, catalogue.size]);
  useRealtime(ds, "actions", state.reload);
  const [until, setUntil] = usePref<Record<string, string>>(`snoozed:${me.userId}`, {});
  const now = Date.now();
  const isSnoozed = useCallback((id: string) => !!until[id] && new Date(until[id]).getTime() > now, [until, now]);
  const value = useMemo<DecisionsCtx>(() => ({
    state,
    open: (state.data ?? []).filter((d) => !isSnoozed(d.id)),
    snoozed: (state.data ?? []).filter((d) => isSnoozed(d.id)),
    snooze: (id, hours = 24) => setUntil({ ...until, [id]: new Date(Date.now() + hours * 3600_000).toISOString() }),
    unsnooze: (id) => { const n = { ...until }; delete n[id]; setUntil(n); },
  }), [state, isSnoozed, until, setUntil]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
